// data/workbookBundles.js
// Turns the catalogue API into the bundle view model the cards render.
//
// This file used to hold BUNDLE_CONFIG: the titles, blurbs, accents, product
// keys and ownership rules for every track, hardcoded, and copied again into
// BundleDetail, BundleLibrary and WorkbookLayout. Four copies of one mapping
// meant a change made in three of them shipped a page that sold the wrong SKU,
// and the price helpers underneath it could only describe two prices for four
// tracks. All of it now comes from /api/bundles.
//
// What is left here is presentation: how a bundle becomes a card. No prices, no
// product codes, no ownership rules of its own. No em or en dashes.

import { useMemo } from "react";
import { routeForBundleSlug, workbookProductFor, rupees } from "../api/catalogue";

// Display-only. The Free Workbooks card shows every workbook that is free to
// read, which is not the same as the "core" bundle's membership: csharp-mindset
// is free to read AND is one of the nine foundation workbooks a C# buyer owns,
// so it belongs to the csharp bundle and appears on the free card too.
//
// This list must never reach a grant or an ownership check. Access comes from
// UserAccesses rows and nothing else; a slug list that decided entitlement is
// how free content ended up excluded from the libraries people had paid for.
export const FREE_SLUGS = ["csharp-mindset", "start-here"];

// The workbooks that belong to a bundle, for display.
// Free is by slug, everything else is by the bundle id the server assigned.
export function workbooksForBundle(bundle, workbooks = []) {
  if (!bundle) return [];
  if (bundle.slug === "core")
    return workbooks.filter((w) => FREE_SLUGS.includes(w.slug));
  return workbooks.filter((w) => w.bundleId === bundle.id);
}

// Weighted star rating across a bundle's workbooks. Null when no reviews.
export function statsForBundle(bundle, workbooks = [], bundleStats = null) {
  let weighted = 0;
  let totalRatings = 0;
  for (const w of workbooksForBundle(bundle, workbooks)) {
    if (w.avgRating != null && w.ratingCount > 0) {
      weighted += w.avgRating * w.ratingCount;
      totalRatings += w.ratingCount;
    }
  }
  const rating =
    totalRatings > 0 ? Math.round((weighted / totalRatings) * 10) / 10 : null;

  // bundleStats is keyed by bundle slug, plus "free" for the total signup count.
  const statsKey = bundle.slug === "core" ? "free" : bundle.slug;
  const enrolled =
    bundleStats && bundleStats[statsKey] != null
      ? bundleStats[statsKey].enrolled
      : undefined;

  return { rating, ratingCount: totalRatings, enrolled };
}

// Ownership: does the caller hold any paid row in this bundle.
// The free bundle belongs to everyone. Everything else is a row, resolved by
// the server, which is the same fact that unlocks the reader. This used to
// compare a single userProduct string against a hardcoded product key, so a
// user who owned two tracks was only recognised for one of them.
export function isBundleOwned(bundle, ownedBundleSlugs = []) {
  if (!bundle) return false;
  if (bundle.slug === "core") return true;
  return ownedBundleSlugs.includes(bundle.slug);
}

// Builds the card view models. Takes the /api/bundles response plus the
// /api/workbooks response (workbooks, ownedBundleSlugs, bundleStats).
export function buildBundles({ bundles, workbooks, ownedBundleSlugs, bundleStats } = {}) {
  const wbs = workbooks ?? [];
  const owned = ownedBundleSlugs ?? [];

  return (bundles ?? []).map((b) => {
    const count = workbooksForBundle(b, wbs).length;
    const { rating, ratingCount, enrolled } = statsForBundle(b, wbs, bundleStats);
    const isOwned = isBundleOwned(b, owned);
    const free = b.slug === "core";

    // The workbook SKU is what a card prices: the card sells workbooks, not the
    // video course. No product means nothing is on sale for this bundle yet,
    // which is what "coming soon" means now. It used to mean "fewer than three
    // workbooks", a rule the frontend invented and the backend never knew.
    const product = free ? null : workbookProductFor(b);
    const comingSoon = !free && (b.comingSoon || !product);

    return {
      // The route param, which keeps /workbooks/bundle/free working.
      track: routeForBundleSlug(b.slug),
      slug: b.slug,
      id: b.id,
      title: b.cardTitle || b.title,
      tag: b.badge,
      blurb: b.blurb,
      accent: b.accent,
      workbooks: count,
      free,
      owned: isOwned,
      comingSoon,
      includesFoundation: b.includesFoundation,
      productCode: product?.code ?? null,
      price: product ? rupees(product.price) : undefined,
      originalPrice: product ? rupees(product.originalPrice) : undefined,
      rating,
      ratingCount,
      enrolled,
    };
  });
}

// Hook wrapper: memoizes buildBundles over the response fields.
export function useWorkbookBundles(data = {}) {
  const { bundles, workbooks, ownedBundleSlugs, bundleStats } = data;
  return useMemo(
    () => buildBundles({ bundles, workbooks, ownedBundleSlugs, bundleStats }),
    [bundles, workbooks, ownedBundleSlugs, bundleStats],
  );
}
